import { create } from 'zustand';
import { customInstance } from '@/api/client';

export interface BillingStatus {
  isSubscribed: boolean;
  planId: string;
  planName?: string;
  allowedFeatures: string[];
  maxStores: number;
  maxConversations: number;
  conversationsUsed: number;
  conversationsTransferred?: number;
  interval?: string | null;
  renewalDate?: string | null;
  nextQuotaResetDate?: string | null;
  NextQuotaResetDate?: string | null;
}

export interface AvailablePlanPrice {
  priceId: string;
  interval: string;
  amount: number;
  currency: string;
}

export interface AvailablePlan {
  planId: string;
  name: string;
  allowedFeatures: string[];
  quotas: Record<string, number>;
  prices: AvailablePlanPrice[];
}

export interface UpgradeModalOptions {
  title?: string;
  description?: string;
}

interface BillingStoreState {
  status: BillingStatus | null;
  isLoading: boolean;
  error: string | null;
  plans: AvailablePlan[] | null;
  isLoadingPlans: boolean;
  plansError: string | null;
  isUpgradeModalOpen: boolean;
  upgradeModalTitle?: string;
  upgradeModalDescription?: string;
  fetchStatus: (workspaceId?: string | null) => Promise<void>;
  fetchPlans: () => Promise<void>;
  openUpgradeModal: (options?: UpgradeModalOptions) => void;
  closeUpgradeModal: () => void;
}

export const useBillingStore = create<BillingStoreState>((set) => ({
  status: null,
  isLoading: false,
  error: null,
  plans: null,
  isLoadingPlans: false,
  plansError: null,
  isUpgradeModalOpen: false,
  upgradeModalTitle: undefined,
  upgradeModalDescription: undefined,
  openUpgradeModal: (options?: UpgradeModalOptions) =>
    set({
      isUpgradeModalOpen: true,
      upgradeModalTitle: options?.title,
      upgradeModalDescription: options?.description,
    }),
  closeUpgradeModal: () =>
    set({
      isUpgradeModalOpen: false,
      // Retain previous title & description during Radix fade-out animation to avoid text popping
    }),
  fetchStatus: async (workspaceId?: string | null) => {
    if (!workspaceId) {
      set({ status: null, isLoading: false, error: null });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      const response = await customInstance<BillingStatus>({
        url: `/api/workspaces/${workspaceId}/billing/status`,
        method: 'GET',
      });
      set({ status: response, isLoading: false });
    } catch (error: any) {
      set({ 
        error: error?.message || 'Falha ao buscar o status de faturamento',
        isLoading: false 
      });
    }
  },
  fetchPlans: async () => {
    set({ isLoadingPlans: true, plansError: null });
    try {
      const response = await customInstance<AvailablePlan[]>({
        url: '/api/billing/plans',
        method: 'GET',
      });
      set({ plans: Array.isArray(response) ? response : [], isLoadingPlans: false });
    } catch (error: any) {
      set({
        plansError: error?.message || 'Falha ao buscar planos de faturamento disponíveis',
        isLoadingPlans: false,
      });
    }
  },
}));
